import type { FactoriesWorkOrder } from "@/api-client";

import {
  HOUR_AGO,
  LAST_WEEK,
  OPERATOR_USER,
  REVIEWER_USER,
  STORYBOOK_ME_USER_AVATAR_URL,
  STORYBOOK_ME_USER_ID,
  STORYBOOK_ME_USER_NAME,
  TWO_HOURS_AGO,
  YESTERDAY,
} from "../../../__fixtures__/factoryPageResponses";
import { confidenceSuitabilitySummary } from "../../../lib/confidenceScore";
import {
  REVIEW_CANDIDATE_COPY,
  githubIssueUrl,
  implementationPlanMarkdown,
  type ReviewCandidate,
  type ReviewCandidateSection,
  type ReviewIssue,
  type ReviewIssuePerson,
} from "./reviewCandidateModel";

function planMarkdownFromSections(sections: ReviewCandidateSection[]): string {
  const requirements = sectionByNumber(sections, "01");
  const criteria = sectionByNumber(sections, "02");
  const files = sectionByNumber(sections, "03");
  const plan = sectionByNumber(sections, "04");

  return implementationPlanMarkdown({
    goal: requirements?.intro ?? "",
    files: files?.items ?? [],
    steps: plan?.items ?? [],
    verify: criteria?.items ?? [],
  });
}

function sectionByNumber(sections: ReviewCandidateSection[], number: string) {
  return sections.find((section) => section.number === number);
}

function withPlanMarkdown(candidate: Omit<ReviewCandidate, "planMarkdown">): ReviewCandidate {
  return { ...candidate, planMarkdown: planMarkdownFromSections(candidate.sections) };
}

const ISSUE_AUTHOR: ReviewIssuePerson = {
  name: STORYBOOK_ME_USER_NAME,
  login: "ldicaprio",
  avatarUrl: STORYBOOK_ME_USER_AVATAR_URL,
};

const ISSUE_ASSIGNEE_ALEX: ReviewIssuePerson = {
  name: REVIEWER_USER.name,
  login: "alex",
};

const ISSUE_ASSIGNEE_JAMIE: ReviewIssuePerson = {
  name: OPERATOR_USER.name,
  login: "jamie",
};

function reviewIssue(ticketKey: string, issue: Omit<ReviewIssue, "url">): ReviewIssue {
  return { ...issue, url: githubIssueUrl(REVIEW_CANDIDATE_COPY.ticketRepository, ticketKey) };
}

function issueMarkdown(summary: string, extra: string): string {
  return `${summary}\n\n${extra}`;
}

export const SENTRY_FACTORY_HTTP_500_URL = "https://superplane.sentry.io/issues/7670162495/";

const SENTRY_FACTORY_HTTP_500_MARKDOWN = `[View in Sentry](${SENTRY_FACTORY_HTTP_500_URL}events/0653054e91914677a2eb40b852d01fee/)

## Summary

- GET /api/v1/factories/{id} hit the factories table right as PR #6671's schema migration added new NOT NULL columns, so the GORM query selected columns that briefly didn't match between deployed code and DB state.
- Sentry event has no exception/stacktrace, only a bare 'HTTP 500' message and request entry.
- Only 1 event, 0 affected users, occurred once and never recurred.
- PR #6671 merged 2026-08-13T15:21:34Z, 18 minutes before firstSeen 15:39:56Z.
- PR #6671 added NOT NULL 'key' and 'next_work_order_number' columns to 'factories' via migration.
- FindFactory/ListLines use GORM struct-based SELECT, which now includes the new Key field on Factory.

## Root Cause

- **File:** [\`pkg/models/factory.go → FindFactory()\`](https://github.com/superplanehq/superplane/blob/b15b021643cf349c7184b6a544a55eab7247e72d/pkg/models/factory.go#L202)
- **Pull Request:** [feat: Work Orders List Page v3 #6671](https://github.com/superplanehq/superplane/pull/6671)

## Suggested Fix

Ensure schema migrations are fully applied and healthy across all DB connections/replicas before rolling out application pods that reference new columns; add a readiness gate or migration-version check to the deploy pipeline, and wrap DescribeFactory's internal errors with sentry.CaptureException including the underlying DB error so future occurrences produce a full stack trace instead of a bare message.

\`\`\`diff
pkg/grpc/actions/factories/errors.go:44-47
- default:
-     return grpcerrors.Internal(err, internalMessage)
+ default:
+     sentry.CaptureException(err)
+     return grpcerrors.Internal(err, internalMessage)
\`\`\`

---
_Auto-generated by the [Sentry Root Cause Analysis](https://app.superplane.com/3ee1aa47-3a60-4c1f-b645-0b9859ab91f8/apps/06b7d04f-5584-46d7-9424-ef79fdfe60ca) app on [SuperPlane](https://superplane.com)._
`;

export const REVIEW_CANDIDATES: ReviewCandidate[] = [
  withPlanMarkdown({
    workOrderId: "wo-review-pay-842",
    ticketKey: "PAY-842",
    title: "Add retry handling to webhook delivery",
    ticketBody:
      "Webhook delivery stops after a transient provider error. Add bounded retries that stay idempotent and keep the delivery audit trail.",
    issue: reviewIssue("PAY-842", {
      createdAt: LAST_WEEK,
      updatedAt: TWO_HOURS_AGO,
      author: ISSUE_AUTHOR,
      assignees: [ISSUE_ASSIGNEE_ALEX],
      labels: [{ name: "reliability" }, { name: "webhooks" }, { name: "ready" }],
      bodyMarkdown: issueMarkdown(
        "Webhook delivery stops after a transient provider error. Add bounded retries that stay idempotent and keep the delivery audit trail.",
        `## Acceptance criteria

- Retry 5xx, 408, and 429 responses up to four attempts.
- Reuse the delivery ID so retries never create a duplicate event.
- Record the last response code and attempt count on final failure.

## Notes

Do not retry permanent 4xx responses, except rate limits and timeouts.`,
      ),
    }),
    confidenceScore: 5,
    confidenceBand: "High",
    reasons: [
      "The GitHub issue names retryable status codes and a hard attempt limit.",
      "The dispatcher and the shared retry utility are mapped, with neighboring tests.",
      "No product decision is open. An agent can implement this in one delivery path.",
    ],
    summary: confidenceSuitabilitySummary("High"),
    readyNote:
      "No implementation has started. Review the completed analysis and approve the plan when you are ready for SuperPlane to begin.",
    sections: [
      {
        number: "01",
        title: "Requirements understood",
        intro:
          "The retry behavior is already established in two adjacent services. SuperPlane found the relevant module, matching test patterns, and no unresolved product decisions.",
        items: [
          "Retry transient webhook failures with bounded exponential backoff.",
          "Keep every retry idempotent and preserve the existing delivery audit trail.",
          "Do not retry permanent 4xx responses, except rate limits and timeouts.",
        ],
      },
      {
        number: "02",
        title: "Acceptance criteria",
        intro: "Mapped from the ticket and repository conventions.",
        items: [
          "5xx, 408, and 429 responses are retried up to four attempts.",
          "Retries use the existing delivery ID and never produce duplicate events.",
          "The final failure records the last response code and attempt count.",
          "Existing successful-delivery behavior and latency remain unchanged.",
        ],
      },
      {
        number: "03",
        title: "Relevant code and tests",
        intro: "Evidence used to assess feasibility and confidence.",
        items: [
          "src/webhooks/dispatcher.ts — Delivery loop and provider error handling",
          "src/shared/retry-policy.ts — Existing backoff pattern used by two services",
          "tests/webhooks/dispatcher.test.ts — 14 neighboring tests cover delivery outcomes",
        ],
      },
      {
        number: "04",
        title: "Implementation plan",
        intro: "The planned path to a review-ready pull request.",
        items: [
          "Add a webhook-specific retry policy using the shared backoff utility.",
          "Classify provider responses as retryable or terminal in the dispatcher.",
          "Reuse the delivery ID across attempts and record attempt metadata.",
          "Add tests for success-after-retry, exhaustion, 429, and permanent 4xx responses.",
        ],
      },
    ],
    noBlockingQuestions: "The analysis found enough context to begin implementation with the plan above.",
  }),
  withPlanMarkdown({
    workOrderId: "wo-review-pay-843",
    ticketKey: "PAY-843",
    title: "Handle duplicate refunds on retry",
    ticketBody:
      "A retry of a successful refund can post a second ledger entry. Treat the same provider key as the original refund.",
    issue: reviewIssue("PAY-843", {
      createdAt: LAST_WEEK,
      updatedAt: YESTERDAY,
      author: ISSUE_AUTHOR,
      assignees: [ISSUE_ASSIGNEE_JAMIE],
      labels: [{ name: "refunds" }, { name: "idempotency" }, { name: "ready" }],
      bodyMarkdown: issueMarkdown(
        "A retry of a successful refund can post a second ledger entry. Treat the same provider key as the original refund.",
        `## Acceptance criteria

- A retry with the same provider key returns the original refund result.
- The ledger gains at most one posted refund for that key.
- A failed first attempt can retry after success without a duplicate post.

## Notes

Reuse the charge idempotency store. Keep the existing refund audit trail.`,
      ),
    }),
    confidenceScore: 5,
    confidenceBand: "High",
    reasons: [
      "The GitHub issue requires one ledger post per provider key.",
      "The refund post path and the charge idempotency store are mapped.",
      "Neighboring tests already cover success and conflict. An agent can reuse them.",
    ],
    summary: confidenceSuitabilitySummary("High"),
    readyNote:
      "No implementation has started. Review the completed analysis and approve the plan when you are ready for SuperPlane to begin.",
    sections: [
      {
        number: "01",
        title: "Requirements understood",
        intro:
          "The ledger already records refund attempts. SuperPlane found the retry path and the duplicate-guard tests.",
        items: [
          "Treat a repeated refund request with the same provider key as the original refund.",
          "Do not create a second ledger entry when the first attempt succeeded.",
          "Keep the existing refund audit trail.",
        ],
      },
      {
        number: "02",
        title: "Acceptance criteria",
        intro: "Mapped from the ticket and repository conventions.",
        items: [
          "A retry with the same idempotency key returns the original refund result.",
          "The ledger gains at most one posted refund for that key.",
          "Failed first attempts can retry without a duplicate post after success.",
        ],
      },
      {
        number: "03",
        title: "Relevant code and tests",
        intro: "Evidence used to assess feasibility and confidence.",
        items: [
          "src/refunds/posting.ts — Refund post and provider key lookup",
          "src/refunds/idempotency.ts — Existing key store used by charges",
          "tests/refunds/posting.test.ts — Neighboring tests cover success and conflict",
        ],
      },
      {
        number: "04",
        title: "Implementation plan",
        intro: "The planned path to a review-ready pull request.",
        items: [
          "Reuse the charge idempotency store for refund retries.",
          "Return the stored result when the key already posted.",
          "Add tests for success-after-retry and duplicate-after-success.",
        ],
      },
    ],
    noBlockingQuestions: "The analysis found enough context to begin implementation with the plan above.",
  }),
  withPlanMarkdown({
    workOrderId: "wo-review-pay-844",
    ticketKey: "PAY-844",
    title: "HTTP 500 /api/v1/factories/0644043d-564b-47e4-95b0-f5be415d0742",
    ticketBody: "GET /api/v1/factories/{id} returned HTTP 500 while PR #6671's factories schema migration rolled out.",
    issue: {
      ...reviewIssue("PAY-844", {
        createdAt: YESTERDAY,
        updatedAt: TWO_HOURS_AGO,
        author: ISSUE_AUTHOR,
        assignees: [ISSUE_ASSIGNEE_ALEX],
        labels: [{ name: "bug" }, { name: "sentry" }, { name: "api" }],
        bodyMarkdown: SENTRY_FACTORY_HTTP_500_MARKDOWN,
      }),
      url: SENTRY_FACTORY_HTTP_500_URL,
    },
    confidenceScore: 4,
    confidenceBand: "High",
    reasons: [
      "The Sentry event names one GET /api/v1/factories/{id} path and a deploy-window schema mismatch.",
      "FindFactory and the factories migration in PR #6671 are mapped.",
      "The event happened once and did not recur. The suggested fix is a readiness gate and better Sentry capture.",
    ],
    summary: confidenceSuitabilitySummary("High"),
    readyNote:
      "No implementation has started. Review the completed analysis and approve the plan when you are ready for SuperPlane to begin.",
    sections: [
      {
        number: "01",
        title: "Requirements understood",
        intro:
          "GET /api/v1/factories/{id} returned HTTP 500 while PR #6671 added NOT NULL columns to factories. The Sentry event has no stack trace.",
        items: [
          "Keep application pods from serving queries that select new factory columns until the migration is healthy on every replica.",
          "Capture the underlying database error in Sentry for DescribeFactory internal failures.",
          "Do not change the factory list or describe contract for successful reads.",
        ],
      },
      {
        number: "02",
        title: "Acceptance criteria",
        intro: "Mapped from the Sentry write-up and the suggested fix.",
        items: [
          "The deploy pipeline waits until the factories migration is applied before it rolls out pods that select the new columns.",
          "DescribeFactory internal errors call sentry.CaptureException with the underlying error.",
          "A successful GET /api/v1/factories/{id} still returns the factory.",
        ],
      },
      {
        number: "03",
        title: "Relevant code and tests",
        intro: "Evidence used to assess feasibility and confidence.",
        items: [
          "pkg/models/factory.go — FindFactory GORM SELECT that includes Key",
          "pkg/grpc/actions/factories/errors.go — Internal error mapping for DescribeFactory",
          "db/migrations — factories key and next_work_order_number from PR #6671",
        ],
      },
      {
        number: "04",
        title: "Implementation plan",
        intro: "The planned path to a review-ready pull request.",
        items: [
          "Add a readiness gate or migration-version check before application rollout.",
          "Call sentry.CaptureException in the DescribeFactory internal-error path.",
          "Confirm GET /api/v1/factories/{id} after a migration deploy.",
        ],
      },
    ],
    noBlockingQuestions: "The analysis found enough context to begin implementation with the plan above.",
  }),
  withPlanMarkdown({
    workOrderId: "wo-review-pay-845",
    ticketKey: "PAY-845",
    title: "Show a clearer empty state on the billing page",
    ticketBody:
      "The billing page empty branch does not tell the user what to do next. Show a short title and one create-invoice action.",
    issue: reviewIssue("PAY-845", {
      createdAt: YESTERDAY,
      updatedAt: HOUR_AGO,
      author: ISSUE_AUTHOR,
      assignees: [],
      labels: [{ name: "ui" }, { name: "billing" }],
      bodyMarkdown: issueMarkdown(
        "The billing page empty branch does not tell the user what to do next. Show a short title and one create-invoice action.",
        `## Acceptance criteria

- A user with no invoices sees the empty state and a create-invoice action.
- A user with invoices still sees the invoice list.
- The empty state uses the existing page header.

## Notes

This change is copy and layout only. Do not add a new billing API.`,
      ),
    }),
    confidenceScore: 3,
    confidenceBand: "Medium",
    reasons: [
      "The GitHub issue names the empty-state title and the create-invoice action.",
      "The billing page already has an empty branch and a shared empty-state component.",
      "The change is copy and layout. An agent can do the work, but the copy is a judgment call.",
    ],
    summary: confidenceSuitabilitySummary("Medium"),
    readyNote:
      "No implementation has started. Review the completed analysis and approve the plan when you are ready for SuperPlane to begin.",
    sections: [
      {
        number: "01",
        title: "Requirements understood",
        intro: "The billing page already has an empty branch. The copy does not tell the user what to do next.",
        items: [
          "Show a short empty-state title and one next action.",
          "Keep the page layout when invoices exist.",
          "Do not add a new billing API.",
        ],
      },
      {
        number: "02",
        title: "Acceptance criteria",
        intro: "Mapped from the ticket and repository conventions.",
        items: [
          "A user with no invoices sees the empty state and a create-invoice action.",
          "A user with invoices still sees the invoice list.",
          "The empty state uses the existing page header.",
        ],
      },
      {
        number: "03",
        title: "Relevant code and tests",
        intro: "Evidence used to assess feasibility and confidence.",
        items: [
          "web/src/pages/Billing.tsx — Invoice list and empty branch",
          "web/src/ui/EmptyState.tsx — Shared empty-state pattern",
          "web/src/pages/Billing.spec.tsx — List and empty coverage",
        ],
      },
      {
        number: "04",
        title: "Implementation plan",
        intro: "The planned path to a review-ready pull request.",
        items: [
          "Replace the empty paragraph with the shared empty-state component.",
          "Add a create-invoice action that opens the existing dialog.",
          "Add a test for the empty-state title and action.",
        ],
      },
    ],
    noBlockingQuestions: "The analysis found enough context to begin implementation with the plan above.",
  }),
  withPlanMarkdown({
    workOrderId: "wo-review-pay-846",
    ticketKey: "PAY-846",
    title: "Upgrade the Node 20 base image",
    ticketBody:
      "The app and worker images still pin Node 18. Move both images to Node 20 and keep the same entrypoint.",
    issue: reviewIssue("PAY-846", {
      createdAt: YESTERDAY,
      updatedAt: HOUR_AGO,
      author: ISSUE_AUTHOR,
      assignees: [ISSUE_ASSIGNEE_ALEX, ISSUE_ASSIGNEE_JAMIE],
      labels: [{ name: "infrastructure" }, { name: "node" }, { name: "ci" }],
      bodyMarkdown: issueMarkdown(
        "The app and worker images still pin Node 18. Move both images to Node 20 and keep the same entrypoint.",
        `## Acceptance criteria

- The app and worker images build on Node 20.
- CI builds both images and runs the existing image smoke tests.
- Runtime environment variables stay the same.

## Notes

Keep the same entrypoint and process user. Do not change application dependencies in this ticket.`,
      ),
    }),
    confidenceScore: 3,
    confidenceBand: "Medium",
    reasons: [
      "The GitHub issue names the Node 20 images and the existing smoke tests.",
      "The change is limited to two Dockerfiles and the image CI job.",
      "Image upgrades can fail at runtime. An agent needs a careful review after the change.",
    ],
    summary: confidenceSuitabilitySummary("Medium"),
    readyNote:
      "No implementation has started. Review the completed analysis and approve the plan when you are ready for SuperPlane to begin.",
    sections: [
      {
        number: "01",
        title: "Requirements understood",
        intro: "Two Dockerfiles pin Node 18. CI already has a Node 20 job for the web package.",
        items: [
          "Move the application and worker images to Node 20.",
          "Keep the same entrypoint and process user.",
          "Do not change application dependencies in this ticket.",
        ],
      },
      {
        number: "02",
        title: "Acceptance criteria",
        intro: "Mapped from the ticket and repository conventions.",
        items: [
          "The app and worker images build on Node 20.",
          "CI builds both images and runs the existing image smoke tests.",
          "Runtime environment variables stay the same.",
        ],
      },
      {
        number: "03",
        title: "Relevant code and tests",
        intro: "Evidence used to assess feasibility and confidence.",
        items: [
          "docker/app.Dockerfile — Application base image",
          "docker/worker.Dockerfile — Worker base image",
          ".github/workflows/images.yml — Image build and smoke tests",
        ],
      },
      {
        number: "04",
        title: "Implementation plan",
        intro: "The planned path to a review-ready pull request.",
        items: [
          "Update both Dockerfiles to the Node 20 base image.",
          "Align the CI image job with the new tag.",
          "Run the existing image smoke tests.",
        ],
      },
    ],
    noBlockingQuestions: "The analysis found enough context to begin implementation with the plan above.",
  }),
];

const REVIEW_CANDIDATES_BY_ID = new Map(REVIEW_CANDIDATES.map((candidate) => [candidate.workOrderId, candidate]));

export function reviewCandidateForWorkOrderId(id: string | undefined): ReviewCandidate | undefined {
  if (!id) {
    return undefined;
  }
  return REVIEW_CANDIDATES_BY_ID.get(id);
}

export const REVIEW_CANDIDATE_WORK_ORDERS: FactoriesWorkOrder[] = REVIEW_CANDIDATES.map((candidate, index) => ({
  id: candidate.workOrderId,
  number: String(842 + index),
  key: candidate.ticketKey,
  title: candidate.title,
  description: candidate.summary,
  state: "STATE_DRAFT",
  result: "RESULT_UNSPECIFIED",
  createdAt: HOUR_AGO,
  updatedAt: HOUR_AGO,
  createdBy: { user: { id: STORYBOOK_ME_USER_ID, name: STORYBOOK_ME_USER_NAME } },
  assignees: [],
  lineDispatches: [],
}));

const BOARD_REVIEW_TICKET_KEYS = new Set(["PAY-842", "PAY-844", "PAY-845"]);

/** Three backlog tickets on the populated line board. Scores are 5, 4, and 3. */
export const BOARD_REVIEW_CANDIDATES = REVIEW_CANDIDATES.filter((candidate) =>
  BOARD_REVIEW_TICKET_KEYS.has(candidate.ticketKey),
);

export const BOARD_REVIEW_CANDIDATE_WORK_ORDERS = REVIEW_CANDIDATE_WORK_ORDERS.filter((order) =>
  BOARD_REVIEW_CANDIDATES.some((candidate) => candidate.workOrderId === order.id),
);
